/**
 * 红级决定（W15）。**只有这五类会让用户决定，其余一律先做再说。**
 *
 * 这一份里最要紧的两条都不是界面：
 *
 *   1. 五类是**穷举的常量**，不是「看着危险就问一下」。一个能自己判断
 *      「这次不用问」的分支，就是一条能被造出来的旁路（CLAUDE.md §8）。
 *   2. 过期的判据只看时间，**不看有没有人来关**。一个要等后台任务
 *      来收尾的状态，会在那个任务挂掉的那天永远开着——而那一天恰好
 *      也是平台不正常的那天。
 */

/** 五类。**一个不多一个不少**，见 SPEC-acceptance §38。 */
export const RED_KINDS = Object.freeze([
  'delete_data',
  'spend_money',
  'access_policy',
  'irreversible_infra',
  'data_migration',
] as const)

export type RedKind = (typeof RED_KINDS)[number]

export const RED_KIND_COPY: Readonly<Record<RedKind, string>> = Object.freeze({
  delete_data: 'lulu_workbench.red.kind_delete_data',
  spend_money: 'lulu_workbench.red.kind_spend_money',
  access_policy: 'lulu_workbench.red.kind_access_policy',
  irreversible_infra: 'lulu_workbench.red.kind_irreversible_infra',
  data_migration: 'lulu_workbench.red.kind_data_migration',
})

/**
 * 这个动作要不要人来点。
 *
 * **收的是穷举出来的 RedKind，不是一个字符串**——类型上就没有
 * 「别的什么危险动作」这一档。想加一类，得先改 RED_KINDS，
 * 而那是一件评审时看得见的事。
 */
export function requiresHuman(kind: RedKind): boolean {
  // 刻意不看任何「用户之前授权过」的入参。设计稿那句
  // 「哪怕你之前授权过一次」说的就是它：一次性授权不能变成长期授权。
  return RED_KINDS.includes(kind)
}

/** 卡片必须回答的八件事，顺序即设计稿顺序。 */
export const RED_QUESTIONS = Object.freeze([
  'lulu_workbench.red.q_what',
  'lulu_workbench.red.q_why',
  'lulu_workbench.red.q_after',
  'lulu_workbench.red.q_undo',
  'lulu_workbench.red.q_backup',
  'lulu_workbench.red.q_alternative',
  'lulu_workbench.red.q_frozen',
  'lulu_workbench.red.q_valid_for',
] as const)

export interface RedOption {
  readonly id: string
  readonly label: string
  /** 这条是不是推荐方案。**推荐的那条当主按钮**，不是「直接删」那条。 */
  readonly recommended: boolean
  /** 走这条会不会毁掉东西。推荐的那条必须是可逆的。 */
  readonly destructive: boolean
}

export interface RedDecision {
  readonly id: string
  readonly kind: RedKind
  /** 决定期间被冻住的写域。**空数组是错的**——见 frozenScopesAreReal。 */
  readonly frozenScopes: readonly string[]
  readonly options: readonly RedOption[]
  /** 这张卡什么时候过期。判据只看它，不看有没有人调过关闭。 */
  readonly expiresAt: string
}

/**
 * 主按钮该是哪一条。
 *
 * 设计稿：**主按钮是推荐方案（如「先导出再删」），不是「直接删」。**
 * 找不到推荐方案时回 `null`——那时不该有主按钮，而不是随便挑一条顶上。
 * 随便挑的那条十有八九是列表里的第一条，而第一条通常就是「直接删」。
 */
export function primaryOption(decision: RedDecision): RedOption | null {
  const rec = decision.options.filter((o) => o.recommended)
  if (rec.length !== 1) return null
  const only = rec[0]
  if (!only || only.destructive) return null
  return only
}

/**
 * 替代方案是必给的，不是可选项。
 *
 * 判据是「除了毁灭性那条之外还有别的」——只给一条「直接删」，
 * 等于没给选择。
 */
export function hasAlternative(decision: RedDecision): boolean {
  return decision.options.some((o) => !o.destructive)
}

/**
 * 写域真的冻了没有。
 *
 * 界面上那句「相关写域已冻结」是一句**关于此刻的断言**，
 * 而一句没有东西撑着的断言，和假 running 是一回事。
 */
export function frozenScopesAreReal(decision: RedDecision): boolean {
  return decision.frozenScopes.length > 0
}

/**
 * 这张卡过期了没有。**只看时间。**
 *
 * 与 CLAUDE.md 里那条 TTL 规矩同源：`closed_at IS NULL AND expires_at > now()`。
 * 只看「有没有人来关」的话，负责关的那个东西挂掉的那天，
 * 这张卡会永远有效——而那一天恰好也是平台不正常的那天。
 */
export function isExpired(decision: RedDecision, now: Date): boolean {
  return new Date(decision.expiresAt).getTime() <= now.getTime()
}

/**
 * 过期之后该怎么办。
 *
 * **只有一种答案：重新问。** 这个函数的返回类型刻意只有这一个值，
 * 而不是一个 `'ask' | 'proceed'` 的联合——留下 `proceed` 那一支，
 * 就是留下一条「过期即默认执行」的路，而它迟早会被谁接上。
 */
export function onExpired(): 'ask_again' {
  return 'ask_again'
}
